import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { ToastContext } from '../App';
import FilesPanel from '../components/FilesPanel';
import TaskChecklist from '../components/TaskChecklist';
import { formatDate, isOverdue } from '../utils/helpers';
import { rub, dt, msLeft, leftTxt, KIND, deadline } from './TradesPage';

// Карточка торгов (как карточка гражданского дела): Главное — срок, задаток и куда платить, шаги, документы лота;
// Задачи — tasks.trade_id; Файлы — trade_files + кнопка «Сформировать: оплата задатка + заявка». 26.10.2026
const muted = { fontSize: 12.5, color: 'var(--text-muted)' };
const reqBox = { background: 'var(--bg-input)', border: '1px dashed var(--accent)', borderRadius: 'var(--radius-sm)', padding: '8px 10px', margin: '6px 0', wordBreak: 'break-word', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13 };
const pre = { whiteSpace: 'pre-wrap', fontSize: 12.5, background: 'var(--bg-input)', padding: 8, borderRadius: 'var(--radius-sm)', maxHeight: 420, overflow: 'auto' };
const TABS = [{ key: 'main', label: 'Главное' }, { key: 'tasks', label: 'Задачи' }, { key: 'files', label: 'Файлы' }];

function Copy({ text }) {
  const [s, setS] = useState('📋 копировать');
  const go = async () => {
    try { await navigator.clipboard.writeText(text); setS('✓ скопировано'); } catch { setS('выделите вручную'); }
    setTimeout(() => setS('📋 копировать'), 2000);
  };
  return <button className="btn btn-secondary btn-sm" type="button" onClick={go}>{s}</button>;
}

// 28.10.2026 владелец: «выставлять период, когда беру» — этап из графика площадки (или вручную) → задача «подать заявку» на его начало
function PlanBox({ t, x, onSaved, showToast }) {
  let sched = [];
  try { sched = JSON.parse((x && x.etp && x.etp.schedule) || '[]').filter(s => msLeft(s[1]) > 0); } catch { sched = []; }
  const [man, setMan] = useState({ from: '', to: '', price: '' });
  const save = async body => {
    try { await api.put(`/trades/${t.id}/plan`, body); showToast(body.from ? 'План сохранён, задача поставлена на начало этапа' : 'План снят'); onSaved(); }
    catch (er) { showToast(er.response?.data?.error || 'Не сохранилось', 'error'); }
  };
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, margin: '10px 0' }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>📅 Когда беру</div>
      {t.plan_from
        ? <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <span>этап с <b>{dt(t.plan_from)}</b>{t.plan_to ? <> по <b>{dt(t.plan_to)}</b></> : ''} мск{t.plan_price ? <> · цена <b>{rub(t.plan_price)}</b></> : ''}</span>
            <span style={muted}>{leftTxt(msLeft(t.plan_from)) === 'срок прошёл' ? 'этап идёт — подавайте' : `до начала: ${leftTxt(msLeft(t.plan_from)).replace('осталось ', '')}`}</span>
            <button className="btn btn-secondary btn-sm" onClick={() => save({ from: '' })}>снять план</button>
          </div>
        : <div style={muted}>план не выбран</div>}
      {sched.length > 0 && <div style={{ marginTop: 8 }}>
        <div style={muted}>{x.etp.schedule_calc ? 'этапы рассчитаны по сообщению Федресурса (сверьте даты на площадке) — нажмите нужный:' : 'этапы по графику площадки — нажмите нужный:'}</div>
        {sched.map((s, i) => (
          <button key={s[0]} className={`btn btn-sm ${t.plan_from === s[0].slice(0, 16) ? 'btn-primary' : 'btn-secondary'}`} style={{ margin: '4px 6px 0 0' }}
            onClick={() => save({ from: s[0], to: s[1], price: s[2] })}>
            {dt(s[0]).slice(0, 5)}–{dt(s[1]).slice(0, 5)} · {rub(s[2])}{i === sched.length - 1 ? ' (последний)' : ''}</button>))}
      </div>}
      <details style={{ marginTop: 8 }} open={!sched.length}>
        <summary style={{ cursor: 'pointer', ...muted }}>указать вручную</summary>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
          <label style={muted}>с <input className="form-input" type="datetime-local" value={man.from} onChange={ev => setMan({ ...man, from: ev.target.value })} /></label>
          <label style={muted}>по <input className="form-input" type="datetime-local" value={man.to} onChange={ev => setMan({ ...man, to: ev.target.value })} /></label>
          <input className="form-input" style={{ width: 160 }} placeholder="цена этапа, ₽" value={man.price} onChange={ev => setMan({ ...man, price: ev.target.value })} />
          <button className="btn btn-primary btn-sm" disabled={!man.from} onClick={() => save(man)}>Сохранить</button>
        </div>
      </details>
    </div>
  );
}

export default function TradeDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'main');
  const [files, setFiles] = useState([]);
  const [tForm, setTForm] = useState({ text: '', task_date: '' });
  const [price, setPrice] = useState('');
  const [deposit, setDeposit] = useState('');
  const [gen, setGen] = useState(false);

  const showToast = useContext(ToastContext);
  const load = useCallback(() => api.get(`/trades/${id}`).then(r => { setT(r.data); setErr(''); }).catch(e => setErr(e.response?.data?.error || e.message)), [id]);
  const loadFiles = useCallback(() => api.get(`/trades/${id}/files`).then(r => setFiles(r.data)).catch(() => {}), [id]);
  useEffect(() => { load(); loadFiles(); }, [load, loadFiles]);
  const run = async (fn, ok) => { try { await fn(); if (ok) showToast(ok); load(); } catch (e) { showToast(e.response?.data?.error || 'Ошибка', 'error'); } };

  if (err) return <div className="card" style={{ color: 'var(--red)' }}>{err}</div>;
  if (!t) return <div className="card">загрузка…</div>;
  const x = t.lot;
  const e = (x && x.etp) || {}, p = (x && x.pay) || {}, end = e.period_end || (x && x.date_finish), dl = deadline(t, end);
  const num = String(t.number || '').split('/')[0];
  const purpose = x ? (x.purpose_text || `Задаток за участие в торгах. Код торгов ${num}, лот № ${t.lot_order}. Должник ${x.bankrupt_name}. НДС не облагается`) : '';
  const step = (sid, done) => run(() => api.post('/trades/step', { guid: t.bidding_guid, lot: t.lot_order, step: sid, done }));
  const genDocs = async () => {
    setGen(true);
    try { await api.post(`/trades/${id}/gen-docs`, { price: price || null, deposit: deposit || null }); showToast('Документы сформированы — папка «Для подачи»'); loadFiles(); }
    catch (er) { showToast(er.response?.data?.error || 'Не сформировалось', 'error'); }
    setGen(false);
  };

  const reject = async () => {
    const reason = window.prompt('Почему не берём? (запишется в заметку лота; задачи по лоту закроются)', '');
    if (reason === null) return;
    try { const r = await api.post(`/trades/${id}/reject`, { reason }); showToast(`Лот — в «Не берём», закрыто задач: ${r.data.closed}`); nav('/trades'); }
    catch (er) { showToast(er.response?.data?.error || 'Не получилось', 'error'); }
  };

  return (
    <div style={{ overflowWrap: 'anywhere' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => nav('/trades')}>← Все торги</button>
        {x && <button className="btn btn-secondary btn-sm" style={{ color: 'var(--red)' }} onClick={reject}>⛔ Не берём</button>}
      </div>
      <div className="page-header">
        <h1 className="page-title">
          <span style={{ fontSize: 12, border: '1px solid var(--border)', borderRadius: 999, padding: '1px 8px', marginRight: 8, color: 'var(--text-muted)', verticalAlign: 'middle' }}>{KIND[t.kind] || ''}</span>
          {t.number} · лот {t.lot_order}
        </h1>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t.title}</div>
      </div>
      {!x && <div className="card" style={{ color: 'var(--red)' }}>Лот больше не отмечен «Берём» на dz.ucfemida.ru — сведения о лоте не загружены. Задачи и файлы сохранены.</div>}

      <div className="tabs" style={{ marginBottom: 14 }}>
        {TABS.map(tb => <button key={tb.key} className={`tab ${tab === tb.key ? 'active' : ''}`} onClick={() => setTab(tb.key)}>
          {tb.label}{tb.key === 'tasks' && t.tasks.filter(z => !z.done).length ? ` (${t.tasks.filter(z => !z.done).length})` : ''}{tb.key === 'files' && files.length ? ` (${files.length})` : ''}</button>)}
      </div>

      {tab === 'main' && x && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ ...muted, flex: 1, minWidth: 0 }}>продаёт: {x.bankrupt_name}{x.case_number ? ` · дело ${x.case_number}` : ''} · {x.trade_place}{x.manager ? ` · АУ: ${x.manager}` : ''}{x.au_email ? ` (${x.au_email})` : ''}</div>
            <span style={{ fontWeight: 700, padding: '4px 10px', borderRadius: 999, background: dl.hot ? 'var(--red-bg)' : 'var(--bg-hover)', color: dl.hot ? 'var(--red)' : undefined }}>
              {dl.text}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '10px 0' }}>
            {x.lot_url && <a className="btn btn-primary btn-sm" href={x.lot_url} target="_blank" rel="noopener noreferrer">{x.lot_direct ? 'Лот на площадке ↗' : `Площадка ↗ (ищите № ${t.number})`}</a>}
            <a className="btn btn-secondary btn-sm" href={`https://fedresurs.ru/biddings/${t.bidding_guid}`} target="_blank" rel="noopener noreferrer">Федресурс ↗</a>
            <a className="btn btn-secondary btn-sm" href={`https://dz.ucfemida.ru/${t.kind === 'dz' ? '' : t.kind}#${t.bidding_guid}:${t.lot_order}`} target="_blank" rel="noopener noreferrer">Разбор Claude ↗</a>
            {e.bids != null && <span style={muted}>заявок на площадке: <b>{e.bids}</b>{e.fetched_at ? ` (проверено ${dt(e.fetched_at)} UTC)` : ''}</span>}
          </div>

          <PlanBox t={t} x={x} onSaved={load} showToast={showToast} />
          <div style={{ border: '2px solid var(--accent)', borderRadius: 'var(--radius)', padding: 12, background: 'var(--accent-light)' }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>💳 Задаток — сколько и куда</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 22, fontWeight: 800 }}>{rub(e.deposit)}</span>
              <span style={muted}>{e.deposit_calc ? `${p.deposit_pct}% от цены этапа (посчитано)` : 'с площадки'} · цена этапа {rub(e.price)}{p.deposit_pct && !e.deposit_calc ? ` · задаток ${p.deposit_pct}%` : ''}</span>
              {e.deposit != null && <Copy text={String(e.deposit).replace('.', ',')} />}
            </div>
            {p.deposit_req
              ? <><div style={muted}>реквизиты (цитата из сообщения Федресурса — сверьте получателя):</div><div style={reqBox}>{p.deposit_req}</div><Copy text={p.deposit_req} /></>
              : <div style={{ color: 'var(--red)', fontWeight: 700 }}>Реквизиты задатка в тексте сообщения не найдены — смотрите объявление.</div>}
            <div style={{ ...muted, marginTop: 8 }}>назначение платежа (готовый текст):</div>
            <div style={reqBox}>{purpose}</div><Copy text={purpose} />
            {p.purpose && <div style={{ ...muted, marginTop: 6 }}>требование организатора: {p.purpose}</div>}
            <div style={muted}>Задаток должен ПОСТУПИТЬ до конца этапа. Готовый документ «Оплата задатка» — во вкладке «Файлы».</div>
          </div>
          {p.purchase_req && <details style={{ marginTop: 10 }}><summary style={{ cursor: 'pointer', fontWeight: 600 }}>💰 Оплата остатка после победы</summary><div style={reqBox}>{p.purchase_req}</div><Copy text={p.purchase_req} /></details>}

          <details open style={{ marginTop: 10 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>✅ Шаги покупки ({x.steps.filter(s => s.done).length} из {x.steps.length})</summary>
            {x.steps.map(s => (
              <label key={s.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0' }}>
                <input type="checkbox" checked={!!s.done} onChange={ev => step(s.id, ev.target.checked)} />
                {s.title}{s.done && <span style={{ color: 'var(--green)', fontSize: 12 }}>✓ {dt(s.done)} UTC</span>}
              </label>))}
          </details>
          <details open style={{ marginTop: 10 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>📄 Документы лота с Федресурса ({x.docs.length})</summary>
            {x.docs.length ? <ul style={{ margin: '6px 0', paddingLeft: 18 }}>{x.docs.map(d => <li key={d.doc_guid}><a href={`/api/trades/doc/${d.doc_guid}`}>{d.file_name}</a></li>)}</ul>
              : <div style={muted}>к сообщению документы не приложены</div>}
          </details>
          {x.rules && <details style={{ marginTop: 10 }}><summary style={{ cursor: 'pointer', fontWeight: 600 }}>📋 Правила подачи заявки (из сообщения)</summary><div style={pre}>{x.rules}</div></details>}
          {x.note && <details style={{ marginTop: 10 }}><summary style={{ cursor: 'pointer', fontWeight: 600 }}>📝 Заметка по лоту</summary><div style={pre}>{x.note}</div></details>}
          {x.message_text && <details style={{ marginTop: 10 }}><summary style={{ cursor: 'pointer', fontWeight: 600 }}>📰 Полный текст объявления</summary><div style={pre}>{x.message_text}</div></details>}
        </div>
      )}

      {tab === 'tasks' && (
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <input className="form-input" style={{ flex: 1, minWidth: 200 }} placeholder="Новая задача по торгам" value={tForm.text} onChange={ev => setTForm({ ...tForm, text: ev.target.value })} />
            <input className="form-input" type="date" style={{ width: 160 }} value={tForm.task_date} onChange={ev => setTForm({ ...tForm, task_date: ev.target.value })} />
            <button className="btn btn-primary" onClick={() => {
              if (!tForm.text.trim()) return;
              run(() => api.post(`/trades/${id}/tasks`, tForm), 'Задача добавлена').then(() => setTForm({ text: '', task_date: '' }));
            }}>Добавить</button>
          </div>
          {t.tasks.length === 0 && <div className="empty-state">Задач пока нет</div>}
          {t.tasks.map(z => (
            <div key={z.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '8px 0', borderTop: '1px solid var(--border)', opacity: z.done ? 0.55 : 1 }}>
              <input type="checkbox" checked={!!z.done} onChange={() => run(() => api.patch(`/tasks/${z.id}/toggle`))} style={{ width: 18, height: 18, marginTop: 2 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, textDecoration: z.done ? 'line-through' : 'none', whiteSpace: 'pre-wrap' }}>{z.text}</div>
                <TaskChecklist key={z.id + '-' + (z.checklist || []).length} task={z} compact onChange={d => { if (!!d.done !== !!z.done) load(); }} />
              </div>
              <div style={{ fontSize: 12, minWidth: 80, color: !z.done && z.task_date && isOverdue(z.task_date) ? 'var(--red)' : 'var(--text-muted)' }}>{z.task_date ? formatDate(z.task_date) : 'без даты'}</div>
              <button className="btn-icon" title="Удалить" onClick={() => { if (window.confirm('Удалить задачу?')) run(() => api.delete(`/tasks/${z.id}`)); }}>✕</button>
            </div>
          ))}
        </div>
      )}

      {tab === 'files' && (
        <>
          <div className="card" style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>📝 Документы для подачи</div>
            <div style={muted}>«Оплата задатка» (куда, сколько, назначение) и «Заявка на участие в торгах» для подписи КЭП — в «Наши документы», папка «Для подачи». Цену предложения можно указать заранее или оставить пустой — впишете сами. Задаток — если платите больше минимального.</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
              <input className="form-input" style={{ width: 200 }} placeholder={`цена, ₽ (этап ${rub(e.price)})`} value={price} onChange={ev => setPrice(ev.target.value)} />
              <input className="form-input" style={{ width: 200 }} placeholder={`задаток, ₽ (мин. ${rub(e.deposit)})`} value={deposit} onChange={ev => setDeposit(ev.target.value)} />
              <button className="btn btn-primary" disabled={gen || !x} onClick={genDocs}>{gen ? 'формирую…' : 'Сформировать документы'}</button>
            </div>
          </div>
          <FilesPanel files={files} loadFiles={loadFiles} showToast={showToast}
            uploadUrl={(kind, folder) => `/trades/${id}/files?kind=${kind}${folder ? `&folder=${encodeURIComponent(folder)}` : ''}`}
            fileHref={f => `/api/trades/files/${f.id}`}
            update={(f, patch) => api.put(`/trades/files/${f.id}`, patch)}
            remove={f => api.delete(`/trades/files/${f.id}`)}
            toLegal={() => Promise.reject({ response: { data: { error: 'Для файлов торгов «В правовую базу» недоступно' } } })} />
        </>
      )}
    </div>
  );
}
